import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Save, Search, UserPlus, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  LINEUP_MAX_APPEARANCES,
  countAppearances,
  describeSlot,
  disciplineOrderFor,
  genderIneligibleReason,
  getLineupFormat,
  isGenderEligible,
  lineupSlotsFor,
  slotMemberLabel,
  validateLineup,
  type Discipline,
  type LineupFormat,
  type LineupPlayerSearchResult,
  type LineupResponse,
  type MatchResponseStatus,
  type MemberGender,
  type SeasonMatchResponse,
  type VotingOverview,
} from '@bm/contracts';
import { api, ApiError } from '../../lib/api';
import { useLockBodyScroll } from '../../lib/use-lock-body-scroll';
import { Alert, Button } from '../ui';
import { LineupStatusBadge } from './LineupStatusBadge';
import { LineupDisciplineSection, type DisciplineInstanceState } from './LineupDisciplineSection';
import { LineupPlayerCard } from './LineupPlayerCard';
import { LineupValidationSummary } from './LineupValidationSummary';
import { PlayerSelectDrawer, type SelectablePlayer } from './PlayerSelectDrawer';

interface PoolPlayer {
  memberId: string;
  name: string;
  gender: MemberGender | null;
  /** hat für dieses Spiel zugesagt (`status === 'accepted'`) */
  accepted: boolean;
}

type SlotState = { discipline: Discipline; position: number; member1Id: string | null; member2Id: string | null };
type DragSource =
  | { kind: 'pool'; memberId: string }
  | { kind: 'slot'; discipline: Discipline; position: number; memberSlot: 1 | 2; memberId: string };
type DrawerTarget = { discipline: Discipline; position: number; memberSlot: 1 | 2 };

const STATUS_PAST: Record<MatchResponseStatus, string> = {
  accepted: 'zugesagt',
  declined: 'abgesagt',
  maybe: 'als "vielleicht" zurückgemeldet',
  pending: 'noch nicht zurückgemeldet',
};

function emptySlots(format: LineupFormat): SlotState[] {
  return lineupSlotsFor(format).map((k) => ({ ...k, member1Id: null, member2Id: null }));
}

/** Vergleichbare Kurzform einer Slot-Liste (gleiche Normalisierung wie das
 * Laden aus `lineup.slots` in den Effekt weiter unten) — für den
 * Änderungs-Vergleich beim Schließen (§ Nutzeranfrage), unabhängig davon,
 * ob die Liste aus dem Server-Stand oder dem lokalen State kommt. */
function slotsSnapshot(
  source: { discipline: Discipline; position: number; member1Id: string | null; member2Id: string | null }[],
  format: LineupFormat,
): string {
  const base = emptySlots(format);
  for (const s of source) {
    const target = base.find((b) => b.discipline === s.discipline && b.position === s.position);
    if (target) {
      target.member1Id = s.member1Id;
      target.member2Id = s.member2Id;
    }
  }
  return JSON.stringify(base);
}

/**
 * Mannschaftsaufstellung planen — eigene, große Planungsansicht als Modal
 * (konsistent mit RosterModal/MatchReportModal/MatchCommentsModal, aber
 * bewusst größer: fast Vollbild auf Mobile, breit auf Desktop). Details zum
 * Konzept: docs/lineup.md.
 *
 * Bedienung funktioniert vollständig ohne Drag&Drop (Tap auf einen Slot
 * öffnet PlayerSelectDrawer — der Kernweg auf jedem Gerät, siehe §6/§21);
 * natives HTML5-Drag&Drop ist ein zusätzlicher Beschleuniger nur für
 * Maus-Nutzer auf größeren Bildschirmen.
 */
export function LineupModal({ match, onClose }: { match: SeasonMatchResponse; onClose: () => void }) {
  const qc = useQueryClient();
  // Vereinsweite Berechtigung ODER additiv Mannschaftsführer für die
  // Mannschaft dieses Spiels — server-berechnet, siehe
  // SeasonMatchResponse.canManage / docs/permissions.md.
  const canEdit = match.canManage;
  // Mini-Mannschaften (Name enthält "M1"/"M2"/… — § Nutzeranfrage) spielen
  // ein kleineres, ungeschlechtliches Format; dieselbe Regel wie serverseitig
  // (`LineupService.resolveFormat`/`VotingService.decorateMatches`).
  const format = getLineupFormat(match.teamName);
  useLockBodyScroll();

  const { data: lineup, isLoading: lineupLoading, error: lineupError } = useQuery({
    queryKey: ['lineup', match.id],
    queryFn: () => api<LineupResponse>(`/season/matches/${match.id}/lineup`, { orgScoped: true }),
  });
  // Selbe Query wie die Karte selbst (['voting', m.id]) — kein Zusatz-Request.
  const { data: overview } = useQuery({
    queryKey: ['voting', match.id],
    queryFn: () => api<VotingOverview>(`/season/matches/${match.id}/voting`, { orgScoped: true }),
  });

  const [slots, setSlots] = useState<SlotState[]>(() => emptySlots(format));
  const [notes, setNotes] = useState('');
  const [dragSource, setDragSource] = useState<DragSource | null>(null);
  const [drawerTarget, setDrawerTarget] = useState<DrawerTarget | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // "Spieler hinzufügen" — manuell über die Suche ergänzte Spieler, die für
  // dieses Spiel noch nicht zugesagt haben (siehe unten `pool`). Nur für
  // die Dauer dieser Sitzung; wird jemand tatsächlich zugewiesen und
  // gespeichert, übernimmt der nächste `lineup`-Reload dieselbe Rolle aus
  // den gespeicherten Slots (member1Name/member1Gender), s. u.
  const [manuallyAdded, setManuallyAdded] = useState<Map<string, { name: string; gender: MemberGender | null }>>(
    new Map(),
  );
  const [playerSearch, setPlayerSearch] = useState('');
  const { data: searchResults } = useQuery({
    queryKey: ['lineup', match.id, 'player-search', playerSearch],
    queryFn: () =>
      api<LineupPlayerSearchResult[]>(
        `/season/matches/${match.id}/lineup/players?q=${encodeURIComponent(playerSearch)}`,
        { orgScoped: true },
      ),
    enabled: playerSearch.trim().length >= 2,
  });

  // Lokal bearbeiten, erst beim Speichern übertragen (§22 Performance) —
  // Server-Stand wird nur beim (Wieder-)Öffnen übernommen, nicht bei jedem
  // Re-Render, sonst würden ungespeicherte Änderungen überschrieben.
  useEffect(() => {
    if (!lineup) return;
    const base = emptySlots(format);
    for (const s of lineup.slots) {
      const target = base.find((b) => b.discipline === s.discipline && b.position === s.position);
      if (target) {
        target.member1Id = s.member1Id;
        target.member2Id = s.member2Id;
      }
    }
    setSlots(base);
    setNotes(lineup.notes ?? '');
    // Bewusst nur an lineup.matchId gebunden (nicht am ganzen `lineup`-Objekt) —
    // ein Re-Fetch mit unverändertem Inhalt darf lokale, noch nicht
    // gespeicherte Änderungen nicht überschreiben.
  }, [lineup?.matchId]);

  const allParticipants = overview?.participants ?? [];
  const acceptedPlayers = allParticipants.filter((p) => p.status === 'accepted');

  // Wer momentan in der GESPEICHERTEN Aufstellung steht, aber (noch) nicht
  // zugesagt hat, kennt der Server trotzdem namentlich (member1Name/
  // member1Gender werden unabhängig vom Rückmeldestatus aufgelöst) — das
  // hält Name/Geschlecht auch nach dem erneuten Öffnen des Modals verfügbar,
  // ohne eine zusätzliche Abfrage.
  const fromSavedLineup = new Map<string, { name: string; gender: MemberGender | null }>();
  for (const s of lineup?.slots ?? []) {
    if (s.member1Id) fromSavedLineup.set(s.member1Id, { name: s.member1Name ?? '—', gender: s.member1Gender });
    if (s.member2Id) fromSavedLineup.set(s.member2Id, { name: s.member2Name ?? '—', gender: s.member2Gender });
  }

  // Verfügbare Spieler = zugesagt ∪ manuell hinzugefügt ∪ bereits (noch ohne
  // Zusage) zugewiesen — zugesagt hat immer Vorrang, damit sich die Farbe
  // automatisch auf Blau/Rosa "richtig" umstellt, sobald jemand tatsächlich
  // zusagt (§ Verein-Anfrage: "sobald der Spieler sich angemeldet hat").
  const poolById = new Map<string, PoolPlayer>();
  for (const p of acceptedPlayers) poolById.set(p.memberId, { memberId: p.memberId, name: p.name, gender: p.gender, accepted: true });
  for (const [memberId, info] of [...fromSavedLineup, ...manuallyAdded]) {
    if (poolById.has(memberId)) continue;
    poolById.set(memberId, { memberId, name: info.name, gender: info.gender, accepted: false });
  }
  const pool = [...poolById.values()].sort((a, b) => a.name.localeCompare(b.name));

  const genderById = new Map(pool.map((p) => [p.memberId, p.gender]));
  const nameById = new Map([...allParticipants.map((p) => [p.memberId, p.name] as const), ...pool.map((p) => [p.memberId, p.name] as const)]);

  const validation = validateLineup(slots, genderById, format);

  const staleWarnings: string[] = [];
  {
    const seen = new Set<string>();
    for (const s of slots) {
      for (const memberId of [s.member1Id, s.member2Id]) {
        if (!memberId || seen.has(memberId)) continue;
        seen.add(memberId);
        const p = allParticipants.find((x) => x.memberId === memberId);
        if (!p) continue; // FK set-null already clears a truly deleted member
        if (p.status !== 'accepted') {
          staleWarnings.push(`Achtung: ${p.name} ist eingeplant, hat das Saisonspiel inzwischen jedoch ${STATUS_PAST[p.status]}.`);
        }
      }
    }
  }

  function applyAssignment(discipline: Discipline, position: number, memberSlot: 1 | 2, source: DragSource) {
    setSlots((prev) => {
      const next = prev.map((s) => ({ ...s }));
      const target = next.find((s) => s.discipline === discipline && s.position === position)!;
      const prevOccupant = memberSlot === 1 ? target.member1Id : target.member2Id;

      if (source.kind === 'slot') {
        const src = next.find((s) => s.discipline === source.discipline && s.position === source.position)!;
        const isSameSlot = src === target && source.memberSlot === memberSlot;
        if (!isSameSlot) {
          if (source.memberSlot === 1) src.member1Id = null;
          else src.member2Id = null;
          // Tausch: wer vorher am Ziel stand, rückt an die frei gewordene
          // Ursprungsposition nach — "innerhalb einer Disziplin austauschen"
          // (§5) statt den bisherigen Besetzer stillschweigend zu verlieren.
          if (source.memberSlot === 1) src.member1Id = prevOccupant;
          else src.member2Id = prevOccupant;
        }
      }
      if (memberSlot === 1) target.member1Id = source.memberId;
      else target.member2Id = source.memberId;
      return next;
    });
  }

  function isValidDropTarget(discipline: Discipline, position: number, memberSlot: 1 | 2): boolean {
    if (!dragSource) return false;
    const gender = genderById.get(dragSource.memberId) ?? null;
    if (!isGenderEligible(discipline, memberSlot, gender)) return false;
    const isSameSlot =
      dragSource.kind === 'slot' &&
      dragSource.discipline === discipline &&
      dragSource.position === position &&
      dragSource.memberSlot === memberSlot;
    if (isSameSlot) return true;
    const currentCount = countAppearances(slots, dragSource.memberId);
    const effectiveCount = dragSource.kind === 'slot' ? currentCount - 1 : currentCount;
    return effectiveCount < LINEUP_MAX_APPEARANCES;
  }

  function removeFromSlot(discipline: Discipline, position: number, memberSlot: 1 | 2) {
    setSlots((prev) =>
      prev.map((s) =>
        s.discipline === discipline && s.position === position
          ? { ...s, [memberSlot === 1 ? 'member1Id' : 'member2Id']: null }
          : s,
      ),
    );
  }

  // Ungespeicherte Änderungen erkennen (§ Nutzeranfrage: "Abfrage ... ob man
  // die bereits konfigurierten Felder verwerfen möchte") — Vergleich gegen
  // den zuletzt vom Server geladenen Stand statt eines separat
  // mitgeführten "war es schon gespeichert"-Flags: `lineup` wird nach
  // erfolgreichem Speichern automatisch neu geladen (`invalidateQueries`
  // unten), sodass der Vergleich danach von selbst wieder "unverändert"
  // ergibt, ohne dass die lokalen `slots`/`notes` extra zurückgesetzt
  // werden müssten.
  const isDirty = !!lineup && (slotsSnapshot(slots, format) !== slotsSnapshot(lineup.slots, format) || notes !== (lineup.notes ?? ''));

  function requestClose() {
    if (isDirty && !confirm('Es gibt ungespeicherte Änderungen an der Aufstellung. Trotzdem schließen und Änderungen verwerfen?')) {
      return;
    }
    onClose();
  }

  const save = useMutation({
    mutationFn: () =>
      api<LineupResponse>(`/season/matches/${match.id}/lineup`, {
        method: 'PUT',
        orgScoped: true,
        body: { notes: notes || null, slots },
      }),
    onSuccess: () => {
      setSaveError(null);
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['lineup', match.id] });
      void qc.invalidateQueries({ queryKey: ['season', 'matches'] });
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (e) => setSaveError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen — bitte erneut versuchen.'),
  });

  const time = match.matchTime ? match.matchTime.slice(0, 5) : null;
  const ownTeamName = match.homeOrAway === 'away' ? match.awayTeam : (match.homeTeam ?? match.awayTeam);
  const opponent = match.homeOrAway === 'away' ? match.homeTeam : match.awayTeam;
  const dateLabel = match.matchDate
    ? new Date(`${match.matchDate}T00:00:00`).toLocaleDateString('de-DE', {
        weekday: 'long',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : 'Termin offen';

  const instancesByDiscipline = Object.fromEntries(
    disciplineOrderFor(format).map((d) => [d, [] as DisciplineInstanceState[]]),
  ) as Record<Discipline, DisciplineInstanceState[]>;
  for (const s of slots) {
    const toPlayer = (id: string | null) =>
      id
        ? {
            memberId: id,
            name: nameById.get(id) ?? '—',
            gender: genderById.get(id) ?? null,
            appearanceCount: countAppearances(slots, id),
          }
        : null;
    instancesByDiscipline[s.discipline].push({
      position: s.position,
      member1: toPlayer(s.member1Id),
      member2: toPlayer(s.member2Id),
    });
  }

  const drawerCurrentMemberId = drawerTarget
    ? (drawerTarget.memberSlot === 1
        ? slots.find((s) => s.discipline === drawerTarget.discipline && s.position === drawerTarget.position)?.member1Id
        : slots.find((s) => s.discipline === drawerTarget.discipline && s.position === drawerTarget.position)?.member2Id) ?? null
    : null;

  const drawerPlayers: SelectablePlayer[] = drawerTarget
    ? pool
        .map((p) => {
          const genderOk = isGenderEligible(drawerTarget.discipline, drawerTarget.memberSlot, p.gender);
          const count = countAppearances(slots, p.memberId);
          const isCurrent = p.memberId === drawerCurrentMemberId;
          const atLimit = count >= LINEUP_MAX_APPEARANCES && !isCurrent;
          return {
            memberId: p.memberId,
            name: p.accepted ? p.name : `${p.name} (noch nicht abgestimmt)`,
            gender: p.gender,
            appearanceCount: count,
            eligible: genderOk && !atLimit,
            ineligibleReason: !genderOk
              ? genderIneligibleReason(drawerTarget.discipline, drawerTarget.memberSlot)
              : atLimit
                ? `Maximale Einsatzzahl (${LINEUP_MAX_APPEARANCES}) erreicht.`
                : undefined,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const searchResultsToShow = (searchResults ?? []).filter((r) => !poolById.has(r.memberId));

  function renderPlayerPool() {
    return (
      <div className="space-y-1.5">
        {pool.length === 0 && (
          <p className="px-1 py-6 text-center text-sm text-slate-400">Noch keine Spieler zugesagt.</p>
        )}
        {pool.map((p) => (
          <LineupPlayerCard
            key={p.memberId}
            player={{
              memberId: p.memberId,
              name: p.name,
              gender: p.gender,
              appearanceCount: countAppearances(slots, p.memberId),
              maxAppearances: LINEUP_MAX_APPEARANCES,
              notResponded: !p.accepted,
            }}
            draggable={canEdit}
            onDragStart={() => setDragSource({ kind: 'pool', memberId: p.memberId })}
            onDragEnd={() => setDragSource(null)}
          />
        ))}

        {canEdit && (
          <div className="pt-2">
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Spieler hinzufügen
            </h4>
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={playerSearch}
                onChange={(e) => setPlayerSearch(e.target.value)}
                placeholder="Name suchen …"
                className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-2.5 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
              />
            </div>
            {playerSearch.trim().length >= 2 && (
              <div className="mt-1.5 space-y-1">
                {searchResultsToShow.length === 0 && (
                  <p className="px-1 py-1.5 text-xs text-slate-400">Keine Treffer.</p>
                )}
                {searchResultsToShow.map((r) => (
                  <button
                    key={r.memberId}
                    type="button"
                    onClick={() => {
                      setManuallyAdded((prev) => new Map(prev).set(r.memberId, { name: r.name, gender: r.gender }));
                      setPlayerSearch('');
                    }}
                    className="flex w-full items-center gap-1.5 rounded-md border border-dashed border-slate-300 px-2.5 py-1.5 text-left text-sm text-slate-600 hover:border-brand-accent hover:bg-brand-accent/5"
                  >
                    <UserPlus size={13} className="shrink-0 text-slate-400" />
                    <span className="truncate">{r.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={requestClose}>
      <div
        className="flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-xl sm:h-auto sm:max-h-[92vh] sm:max-w-5xl sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Kopfbereich — kompakt, alle Pflichtangaben aus §1 */}
        <div className="shrink-0 border-b border-slate-100 px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-slate-800">
                {ownTeamName ?? '—'} <span className="font-normal text-slate-400">vs.</span> {opponent ?? '—'}
              </h2>
              <p className="text-xs text-slate-500">
                {dateLabel}
                {time ? ` · ${time} Uhr` : ''}
                {match.homeOrAway && (match.homeOrAway === 'home' ? ' · Heimspiel' : ' · Auswärtsspiel')}
                {match.venue ? ` · ${match.venue}` : ''}
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <Users size={12} />
                  {overview?.counts.accepted ?? 0} Spieler zugesagt
                </span>
                <LineupStatusBadge status={validation.status} />
              </p>
            </div>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Schließen"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Inhalt */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {lineupLoading && <p className="text-sm text-slate-400">Lädt…</p>}
          {lineupError && (
            <Alert>
              {lineupError instanceof ApiError
                ? lineupError.message
                : 'Aufstellung konnte nicht geladen werden — bitte später erneut versuchen.'}
            </Alert>
          )}

          {!canEdit && (
            <div className="mb-3">
              <Alert kind="success">Nur Ansicht — du hast keine Berechtigung, diese Aufstellung zu bearbeiten.</Alert>
            </div>
          )}
          {staleWarnings.length > 0 && (
            <div className="mb-3 space-y-1.5">
              {staleWarnings.map((w, i) => (
                <Alert key={i}>{w}</Alert>
              ))}
            </div>
          )}

          {!lineupLoading && !lineupError && (
            <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-4">
              {/* Verfügbare Spieler — auf Desktop immer sichtbar links, auf
                  Mobile einklappbar (native <details>, kein Zusatz-JS). */}
              <div className="hidden lg:block">
                <h3 className="mb-2 text-sm font-semibold text-slate-700">Verfügbare Spieler</h3>
                {renderPlayerPool()}
              </div>
              <details className="mb-4 rounded-lg border border-slate-200 lg:hidden" open>
                <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2.5 text-sm font-semibold text-slate-700">
                  <span>Verfügbare Spieler ({pool.length})</span>
                  <ChevronDown size={16} className="text-slate-400" />
                </summary>
                <div className="border-t border-slate-100 p-3">{renderPlayerPool()}</div>
              </details>

              {/* Mannschaftsaufstellung */}
              <div className="space-y-2.5">
                <LineupValidationSummary result={validation} />
                {disciplineOrderFor(format).map((discipline) => (
                  <LineupDisciplineSection
                    key={discipline}
                    discipline={discipline}
                    instances={instancesByDiscipline[discipline]}
                    disabled={!canEdit}
                    isDragActive={!!dragSource}
                    isValidDropTarget={(position, memberSlot) => isValidDropTarget(discipline, position, memberSlot)}
                    onOpenDrawer={(position, memberSlot) => {
                      if (canEdit) setDrawerTarget({ discipline, position, memberSlot });
                    }}
                    onRemove={(position, memberSlot) => removeFromSlot(discipline, position, memberSlot)}
                    onDrop={(position, memberSlot) => {
                      if (dragSource) {
                        applyAssignment(discipline, position, memberSlot, dragSource);
                        setDragSource(null);
                      }
                    }}
                  />
                ))}

                {canEdit && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-600">Notizen (optional)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      maxLength={2000}
                      placeholder="z. B. Hinweise zur Aufstellung, Ausfälle kurzfristig geklärt …"
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Speichern — sticky, aber schmal (§8/§13) */}
        {canEdit && !lineupLoading && !lineupError && (
          <div className="shrink-0 border-t border-slate-100 bg-white px-4 py-2.5">
            {saveError && (
              <div className="mb-2">
                <Alert>{saveError}</Alert>
              </div>
            )}
            {saved && (
              <div className="mb-2">
                <Alert kind="success">Mannschaftsaufstellung erfolgreich gespeichert.</Alert>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={requestClose}>
                Schließen
              </Button>
              <Button type="button" className="gap-2" disabled={save.isPending} onClick={() => save.mutate()}>
                <Save size={16} />
                {save.isPending ? 'Speichert…' : 'Aufstellung speichern'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {drawerTarget && (
        <PlayerSelectDrawer
          title={`Spieler auswählen — ${describeSlot(drawerTarget.discipline, drawerTarget.position)}`}
          subtitle={`Position: ${slotMemberLabel(drawerTarget.discipline, drawerTarget.memberSlot)}`}
          players={drawerPlayers}
          currentMemberId={drawerCurrentMemberId}
          onClear={
            drawerCurrentMemberId
              ? () => {
                  removeFromSlot(drawerTarget.discipline, drawerTarget.position, drawerTarget.memberSlot);
                  setDrawerTarget(null);
                }
              : undefined
          }
          onSelect={(memberId) => {
            applyAssignment(drawerTarget.discipline, drawerTarget.position, drawerTarget.memberSlot, {
              kind: 'pool',
              memberId,
            });
            setDrawerTarget(null);
          }}
          onClose={() => setDrawerTarget(null)}
        />
      )}
    </div>
  );
}
